import { memo, useEffect } from "react";
import { useContent } from "../../../../../contexts/contentContext/contentContext";
import AddonIcon from "./components/AddonIcon";
import useAddon from "../../hooks/useAddon";
import useCurrency from "../../hooks/useCurrency";
import {
  CurrencyE,
  CurrencySymbolE,
  PackagePeriodsE,
} from "../../models/enums";
import IncreaseDecreaseBox from "../../../../Elements/IncreaseDecreaseBox/IncreaseDecreaseBox";
import type { PackageTypeRouteT } from "../../models/models";
import { useParams } from "react-router-dom";
import NewAddonPrice from "./components/NewAddonPrice";
import { useUser } from "../../../../../contexts/userContext/userContext";
import Tooltip from "../../../../Elements/Tooltip/Tooltip";
import Image from "../../../../Elements/Image/Image";
import LoadingContainerElement from "../../../../Elements/LoadingContainerElement/LoadingContainerElement";
import {
  MAX_EXTRA_STORAGE_DEFAULT_VALUE_IN_TB,
  MAX_SUB_USERS_DEFAULT_VALUE,
} from "./helpers/constants";
import useMediaQueries from "../../../../../hooks/useMediaQueries";

import styles from "./styles.module.scss";

const PurchaseAddonSection = memo(() => {
  const { packageType } = useParams<{
    packageType: PackageTypeRouteT;
  }>();
  const { getTextByKey } = useContent();
  const {
    addonQuantity,
    setAddonQuantity,
    addonData,
    fetchAddonData,
    isFetchingAddonData,
    existingAddonQuantity,
  } = useAddon();
  const { currency } = useCurrency();
  const { getUserBillingDetails, getUserData } = useUser();
  const { mobileCondition } = useMediaQueries();

  useEffect(() => {
    // Fetch data about addon price.
    fetchAddonData();
  }, []);

  useEffect(() => {
    // Set existing addon quantity as default value for the new quantity.
    setAddonQuantity(existingAddonQuantity || 1);
  }, []);

  if (!addonData) {
    return <></>;
  }

  const maxSubUsers = getUserData()?.MaxSubUsers || MAX_SUB_USERS_DEFAULT_VALUE;

  if (isFetchingAddonData) {
    return <LoadingContainerElement />;
  }

  return (
    <div className={styles.addonsPurchaseContainer}>
      <div
        className={`${styles.amountOfAddons} ${
          !existingAddonQuantity ? styles.notExistsAddons : ""
        }`}
      >
        <div className={styles.sideContainer}>
          <AddonIcon />
          {!!existingAddonQuantity ? (
            <div className={styles.textContainer}>
              <div className={styles.title}>
                {getTextByKey("current-quanity")}
              </div>
              <div className={styles.subTitle}>
                {getTextByKey(`total-amout-${packageType}`)}{" "}
                {existingAddonQuantity}
              </div>
              <div className={styles.subTitle}>
                {getTextByKey("monthly-cost")}{" "}
                {addonData[currency]! * existingAddonQuantity}
                {CurrencySymbolE[currency]}
              </div>
            </div>
          ) : (
            <div className={styles.textContainer}>
              <div className={styles.title}>
                {getTextByKey(`amount-of-${packageType}`)}
              </div>
              <div className={styles.subTitle}>
                {CurrencySymbolE[currency]}
                {addonData[currency]} {getTextByKey(`price-per-${packageType}`)}
              </div>
            </div>
          )}
        </div>
        {!existingAddonQuantity && (
          <div className={styles.licenseSelectorWrapper}>
            <IncreaseDecreaseBox
              value={addonQuantity}
              setValue={setAddonQuantity}
              // Not allow to do downgrade if current quantity is already zero
              minValue={existingAddonQuantity > 0 ? 0 : 1}
              maxValue={
                packageType === "sub-users"
                  ? maxSubUsers
                  : MAX_EXTRA_STORAGE_DEFAULT_VALUE_IN_TB
              }
            />
            {+addonQuantity ===
            +(packageType === "sub-users"
              ? maxSubUsers
              : MAX_EXTRA_STORAGE_DEFAULT_VALUE_IN_TB) ? (
              !mobileCondition ? (
                <div className={styles.limitExceededIcon}>
                  {/*@ts-ignore*/}
                  <Tooltip
                    id="sub-users-max-limit"
                    tooltipTextKey={`${packageType}-limit-can-not-be-exceed`}
                    top
                  >
                    <Image name="create-form/exclamation-icon" title="" />
                  </Tooltip>
                </div>
              ) : null
            ) : null}
          </div>
        )}
        {+addonQuantity ===
        +(packageType === "sub-users"
          ? maxSubUsers
          : MAX_EXTRA_STORAGE_DEFAULT_VALUE_IN_TB) ? (
          mobileCondition ? (
            <div
              className={styles.limitExceedText}
            >{getTextByKey(`${packageType}-limit-can-not-be-exceed`)}</div>
          ) : null
        ) : null}
      </div>
      <div className={styles.newPriceContainer}>
        <div className={styles.totalPriceContainer}>
          {!!existingAddonQuantity ? <NewAddonPrice /> : null}
          {!existingAddonQuantity && (
            <>
              <div className={styles.textContainer}>
                <div className={styles.title}>
                  {getTextByKey("total-price")}
                </div>
                <div className={styles.subTitle}>
                  {currency}/{getTextByKey("month")}
                </div>
              </div>
              <div className={styles.totalPrice}>
                {CurrencySymbolE[currency]}
                {(addonData?.[currency || CurrencyE.ILS] || 0) *
                  (addonQuantity || 0)}
              </div>
            </>
          )}
        </div>
        {!(
          getUserBillingDetails()?.period === PackagePeriodsE.YEARLY &&
          !addonQuantity
        ) ? (
          <div className={styles.addonFirstMonthText}>
            <p>{getTextByKey("first-month-calc-price")}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default PurchaseAddonSection;
